import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserIsLoading, setAppConfig, setIsAuthorized, setUserIsLoading } from "../reducers/main/mainReducer";
import { DataProviderInstance } from "../@shared/services/DataProvider";
import { SchemaProviderInstance } from "../@shared/services/SchemaProvider";
import { TrainedModelProviderInstance } from "../@shared/services/TrainedModelProvider";
import { UserProviderInstance } from "../@shared/services/UserProvider";
import { onThrowAlert } from "../sagas/dataSaga/dataSagaActions";
import { NotificationModel } from "../reducers/main/mainModel";
import { ConfigurationProviderInstance } from "../@shared/services/ConfigurationProvider";
import { setIsSubscriptionDialogOpen } from "../reducers/subscription/subscriptionReducer";

export type UseAuthResult = {
    shouldHideComponents: boolean;
    isInitialized: boolean;
    isUserLoading: boolean;
}

export const useAuth = () => {
    const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
    const hideComponentsOnRoutes = useMemo(() => ["/auth", "/login"], []);
    const isUserLoading = useSelector(getUserIsLoading)
    const navigate = useNavigate();
    const location = useLocation();
    const shouldHideComponents = useMemo(() => hideComponentsOnRoutes.includes(location.pathname), [hideComponentsOnRoutes, location.pathname]);

    const dispatch = useDispatch();

    React.useEffect(() => {
        SchemaProviderInstance.getInstance();
        DataProviderInstance.init();
        TrainedModelProviderInstance.init();
        UserProviderInstance.init();

        (async () => {
            const config = await ConfigurationProviderInstance.get();
            dispatch(setAppConfig(config.data))
            if (!shouldHideComponents && !isInitialized) {
                dispatch(setUserIsLoading(true));

                try {
                    const currentUser = await UserProviderInstance.current()
                    if (currentUser.status === 200) {
                        dispatch(setIsAuthorized(currentUser.data));
                        if(currentUser.data.licenseKey === null){
                            dispatch(setIsSubscriptionDialogOpen(true));
                        }
                    }
                    else {
                        dispatch(setIsAuthorized(undefined));
                        navigate('/login');
                    }
                }
                catch (error) {
                    dispatch(onThrowAlert({
                        message: (error as Error).message,
                        lifeTime: 10000
                    } as NotificationModel));
                }
                finally {
                    dispatch(setUserIsLoading(false));
                }
            }
            else if (shouldHideComponents && !isInitialized) {
                dispatch(setUserIsLoading(false));
            }
        })()

        setIsInitialized(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        shouldHideComponents,
        isInitialized,
        isUserLoading
    }
}